import { useEffect, useRef, useState } from 'react'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { useAuth } from '@/providers/AuthProvider'
import { useReturnToService } from '@/hooks/useEquipment'
import { useTenantSigners } from '@/hooks/useSigners'
import { PERMISSIONS } from '@/lib/permissions'
import { apiErrorMessage } from '@/lib/apiErrorMessage'

/** Mirrors `@MaxLength` on the server's ReturnToServiceDto. */
const REASON_MAX_LENGTH = 2000

/**
 * Out of service → Available by hand (z8nrz7fzyk), laid out as the client's
 * mock: the form sign-off dialog with a required reason above it. The reason
 * and the signature (anyone listed may sign, "Sign as") go into the unit's
 * audit trail. Completing the overdue PM still brings a unit back on its own.
 */
export function ReturnToServiceDialog({
  unit,
  onClose,
}: {
  unit: { id: string; label: string } | null
  onClose: () => void
}) {
  const { user } = useAuth()
  const open = unit !== null
  const returnToService = useReturnToService()
  const roster = useTenantSigners(PERMISSIONS.equipmentRead, open)
  // Free text only, starting empty (client): no suggested reason, not even a
  // placeholder that could read as one.
  const [reason, setReason] = useState('')
  // Read at send time, not from the render that started signing: the password
  // check is async, and text typed meanwhile must not be dropped.
  const reasonRef = useRef('')
  const [error, setError] = useState<string | null>(null)

  // Each return is its own deliberate act: never prefill the last reason.
  useEffect(() => {
    if (open) {
      setReason('')
      reasonRef.current = ''
      setError(null)
    }
  }, [open])

  const confirm = async (signingToken: string) => {
    if (!unit) return
    setError(null)
    try {
      await returnToService.mutateAsync({ id: unit.id, payload: { signingToken, reason: reasonRef.current.trim() } })
      onClose()
    } catch (err) {
      setError(apiErrorMessage(err, 'The unit could not be put back in service.'))
    }
  }

  return (
    <SignatureDialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title='Change Status from Out of Service'
      description={unit ? `${unit.label} becomes Available. Enter your password to sign off.` : undefined}
      confirmLabel='Sign & Complete'
      signerName={user?.fullName || user?.email || ''}
      confirmDisabled={reason.trim() === ''}
      busy={returnToService.isPending}
      error={error}
      signAs={{
        mode: 'optional',
        options: roster.data,
        loading: roster.isLoading,
        error: roster.isError ? 'Could not load the list of signers.' : null,
        selfId: user?.userId ?? null,
      }}
      onConfirm={(token) => void confirm(token)}
    >
      <div className='space-y-1.5'>
        <Label htmlFor='return-to-service-reason'>
          Enter a reason <span className='text-destructive'>*</span>
        </Label>
        <Textarea
          id='return-to-service-reason'
          rows={3}
          maxLength={REASON_MAX_LENGTH}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value)
            reasonRef.current = e.target.value
          }}
          autoFocus
        />
      </div>
    </SignatureDialog>
  )
}
